import React, { useRef, useState } from 'react';
import { axiosInstance } from '../../App';
import { toast } from 'sonner';
import { X, Upload, FilePlus, Loader2 } from 'lucide-react';


const KIND_OPTIONS = [
  ['supplier_invoice', 'فاتورَة مورِد'],
  ['customer_invoice', 'فاتورَة عَميل'],
  ['receipt_voucher',  'سَنَد قَبض'],
  ['payment_voucher',  'سَنَد صَرف'],
  ['bank_statement',   'كَشف بَنكي'],
  ['contract',         'عَقد'],
  ['purchase_order',   'أَمر شِراء'],
  ['delivery_note',    'سَنَد تَسليم'],
  ['credit_note',      'إشعار دائِن'],
  ['debit_note',       'إشعار مَدين'],
  ['expense_receipt',  'فاتورَة مَصروف'],
  ['attachment',       'مُرفَق'],
  ['other',            'أُخرى'],
];


export default function DocUploadDialog({ onClose, onUploaded }) {
  const inputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [meta, setMeta] = useState({
    kind: 'other',
    title: '',
    reference_no: '',
    related_party_name: '',
    document_date: '',
    amount: '',
    currency: 'SAR',
    tags: '',
  });

  const onPick = (e) => {
    const list = Array.from(e.target.files || []);
    setFiles((cur) => [...cur, ...list]);
    e.target.value = '';
  };

  const remove = (i) => setFiles((cur) => cur.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!files.length) { toast.error('أَضِف ملَفاً واحِداً على الأَقَل'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('kind', meta.kind);
      if (meta.title)              fd.append('title', meta.title);
      if (meta.reference_no)       fd.append('reference_no', meta.reference_no);
      if (meta.related_party_name) fd.append('related_party_name', meta.related_party_name);
      if (meta.document_date)      fd.append('document_date', meta.document_date);
      if (meta.amount)             fd.append('amount', meta.amount);
      if (meta.currency)           fd.append('currency', meta.currency);
      if (meta.tags)               fd.append('tags', meta.tags);
      for (const f of files) fd.append('files', f);
      const { data } = await axiosInstance.post('/document-hub', fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`تَم رَفع ${data.uploaded} مُستَنَد`);
      onUploaded?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فَشَل الرَّفع');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="upload-dialog">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" dir="rtl">
        <div className="flex items-center justify-between px-5 py-3 border-b bg-emerald-50">
          <div className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-emerald-600" />
            <div className="text-base font-bold">رَفع مُستَنَد جَديد</div>
          </div>
          <button onClick={onClose} data-testid="upload-close" className="p-1.5 hover:bg-slate-100 rounded-lg">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          {/* Files */}
          <div>
            <label className="text-xs font-semibold text-slate-700">المَلَفات:</label>
            <div className="mt-1 rounded-xl border-2 border-dashed border-slate-300 p-4 text-center cursor-pointer hover:bg-slate-50"
                  onClick={() => inputRef.current?.click()}>
              <FilePlus className="w-8 h-8 mx-auto text-slate-400 mb-1" />
              <div className="text-xs text-slate-600">اِنقُر لاختيار ملَف (أَو عِدَّة ملَفات)</div>
              <div className="text-[10px] text-slate-400 mt-1">PDF / صور / Office / أَيّ نَوع</div>
              <input ref={inputRef} type="file" multiple onChange={onPick} className="hidden" />
            </div>
            {files.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="upload-files">
                {files.map((f, i) => (
                  <span key={i} className="inline-flex items-center gap-1 text-[11px] bg-slate-100 rounded-full px-2 py-0.5">
                    <span className="font-mono">{f.name}</span>
                    <span className="text-[10px] text-slate-500">({(f.size / 1024).toFixed(1)} KB)</span>
                    <button onClick={() => remove(i)} className="text-slate-400 hover:text-red-600">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Kind */}
          <div>
            <label className="text-xs font-semibold text-slate-700">النَّوع:</label>
            <select value={meta.kind} onChange={(e) => setMeta({ ...meta, kind: e.target.value })}
                     data-testid="upload-kind"
                     className="w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm mt-1">
              {KIND_OPTIONS.map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </div>

          {/* Title (if single file) */}
          {files.length <= 1 && (
            <div>
              <label className="text-xs font-semibold text-slate-700">العُنوان (اختياري):</label>
              <input value={meta.title} onChange={(e) => setMeta({ ...meta, title: e.target.value })}
                     placeholder="عُنوان وَصفي" data-testid="upload-title"
                     className="w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm mt-1" />
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-700">رَقم المَرجِع:</label>
              <input value={meta.reference_no} onChange={(e) => setMeta({ ...meta, reference_no: e.target.value })}
                     placeholder="INV-2026-001" data-testid="upload-ref"
                     className="w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm mt-1 font-mono" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700">المورد / الطَّرف:</label>
              <input value={meta.related_party_name} onChange={(e) => setMeta({ ...meta, related_party_name: e.target.value })}
                     placeholder="اسم الطَّرف الآخَر" data-testid="upload-party"
                     className="w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm mt-1" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700">تاريخ المُستَنَد:</label>
              <input type="date" value={meta.document_date}
                     onChange={(e) => setMeta({ ...meta, document_date: e.target.value })}
                     data-testid="upload-date"
                     className="w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm mt-1" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700">المَبلَغ:</label>
              <div className="flex items-center gap-1 mt-1">
                <input type="number" step="0.01" value={meta.amount}
                       onChange={(e) => setMeta({ ...meta, amount: e.target.value })}
                       placeholder="0.00" data-testid="upload-amount"
                       className="flex-1 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-mono" />
                <input value={meta.currency}
                       onChange={(e) => setMeta({ ...meta, currency: e.target.value })}
                       className="w-16 rounded-lg border border-slate-300 px-2 py-1.5 text-sm font-mono"
                       data-testid="upload-currency" />
              </div>
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-700">الوُسوم (مَفصولَة بِفاصِلَة):</label>
            <input value={meta.tags} onChange={(e) => setMeta({ ...meta, tags: e.target.value })}
                   placeholder="ضَريبَة, 2026, مُورِد-أ"
                   data-testid="upload-tags"
                   className="w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm mt-1 font-mono" />
          </div>
        </div>
        <div className="px-5 py-3 border-t bg-slate-50 flex items-center justify-end gap-2">
          <button onClick={onClose} data-testid="upload-cancel"
                  className="text-sm font-semibold rounded-lg bg-white border border-slate-300 hover:bg-slate-100 px-4 py-2">
            إلغاء
          </button>
          <button onClick={submit} disabled={busy || !files.length}
                  data-testid="upload-submit"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {busy ? '…' : 'رَفع'}
          </button>
        </div>
      </div>
    </div>
  );
}
